import React, { useState } from "react";
import { Mail, X } from "lucide-react";
import { useAuth } from "./Context/AuthContext";
import { sendEmailLink } from "./lib/firebase";

interface AuthModalProps {
  isOpen: boolean;
  setIsOpen: () => void;
}

const AuthModal: React.FC<AuthModalProps> = ({ isOpen, setIsOpen }) => {
  const { signInWithGoogle } = useAuth();
  const [email, setEmail] = useState<string>("");
  const [statusMessage, setStatusMessage] = useState<string | null>(null);   
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState({
    email: false,
    google: false,
    anonymous: false,
  });

  const handleSignIn = async (type: "email" | "google" | "anonymous") => {
    setStatusMessage(null);
    setHasError(false);
    setIsLoading((prev) => ({ ...prev, [type]: true }));

    try {
      if (type === "email") {
        if (!email) throw new Error("Please enter your email");

        const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!regex.test(email)) throw new Error("Invalid email address");

        await sendEmailLink(email);
        setStatusMessage("✓ Login link sent to your email");
        localStorage.setItem("emailForSignIn", email);
        setEmail("");
      } else if (type === "google") {
        await signInWithGoogle();
        setStatusMessage("Successfully signed in with Google");
        window.location.reload();
        setIsLoading({email: false, google: false, anonymous: false});
      }

      setHasError(false);
    } catch (err: any) {
      setHasError(true);
      setStatusMessage(err.message || "Something went wrong. Please try again.");
    } finally {
      setIsLoading((prev) => ({ ...prev, [type]: false }));
    }
  };


  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div
        onClick={setIsOpen}
        className="fixed inset-0 bg-black/50 z-[1000] animate-fade"
      />

      {/* Modal */}
      <div className="fixed inset-x-0 bottom-0 lg:inset-0 lg:flex lg:items-center lg:justify-center z-[1100] animate-slide-up">
        <div className="bg-white rounded-t-3xl lg:rounded-2xl shadow-2xl w-full h-[75vh] lg:h-auto lg:max-w-md lg:w-full">
          {/* Header */}
          <div className="relative p-6 border-b border-gray-200">
            <button
              onClick={setIsOpen}
              className="absolute right-4 top-6 p-2 hover:bg-gray-100 rounded-full"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
            <h2 className="text-2xl font-bold text-gray-900">Welcome</h2>
            <p className="text-sm text-gray-600">Sign in to continue</p>
          </div>

          {/* Body */}
          <div className="px-6 py-2 overflow-y-auto max-h-[calc(75vh-100px)] lg:max-h-none">
            {statusMessage && (
                  <span className={`text-sm ${hasError ? "text-red-600" : "text-green-600"} block`}>
                    {statusMessage}
                  </span>
            )}

            {/* Email */}
            <div className="space-y-3 mb-4">
              <label className="text-sm font-medium text-gray-700">
                Email address
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none"
              />

              <button
                onClick={() => handleSignIn("email")}
                disabled={isLoading.email}
                className="w-full bg-indigo-600 text-white py-3 rounded-lg font-medium hover:bg-indigo-700 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <Mail className="w-5 h-5" />
                {isLoading.email ? "Sending..." : "Continue with Email"}
              </button>
            </div>

            {/* Divider */}
            <div className="relative mb-4">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-gray-500">
                  Or continue with
                </span>
              </div>
            </div>

            {/* Google */}
            <button
              onClick={() => handleSignIn("google")}
              className="w-full flex items-center justify-center gap-3 px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium text-gray-700 mb-4"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              Continue with Google
            </button>

            {/* Hidden OAuth buttons (Apple/Facebook) */}
            {/* Uncomment later */}
            {/* <button>Continue with Apple</button> */}

            <p className="text-xs text-center text-gray-500 pt-4">
              By continuing, you agree to our Terms of Service and Privacy
              Policy.
            </p>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes slide-up {
          from { transform: translateY(100%); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes fade {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .animate-slide-up { animation: slide-up 0.25s ease-out; }
        .animate-fade { animation: fade 0.25s ease-in-out; }
      `}</style>
    </>
  );
};

export default AuthModal;
